import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Paper, TextField, Grid, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { useNavigate } from 'react-router-dom';

const CreatePurchaseOrderPage = () => {
    const navigate = useNavigate();
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [formData, setFormData] = useState({
        supplierId: '',
        orderDate: new Date().toISOString().split('T')[0],
        expectedDate: '',
        notes: '',
        status: 'PENDING'
    });
    const [items, setItems] = useState<any[]>([]);

    const getHeaders = useCallback(() => {
        const userInfo = localStorage.getItem('userInfo');
        const token = userInfo ? JSON.parse(userInfo).token : null;
        return { headers: { Authorization: `Bearer ${token}` } };
    }, []);

    const fetchData = useCallback(async () => {
        try {
            const headers = getHeaders();
            const [suppliersRes, productsRes] = await Promise.all([
                axios.get(getApiUrl('suppliers'), headers),
                axios.get(getApiUrl('inventory'), headers)
            ]);
            setSuppliers(suppliersRes.data);
            setProducts(productsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [getHeaders]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Quick Add Product State
    const [newProductOpen, setNewProductOpen] = useState(false);
    const [newProductData, setNewProductData] = useState({
        name: '',
        sku: '',
        category: 'General',
        costPrice: '',
        price: ''
    });

    const handleCreateProduct = async () => {
        try {
            const payload = {
                ...newProductData,
                sku: newProductData.sku || `SKU-${Date.now()}`, // Auto-generate if empty
                type: 'GOODS',
                stockQuantity: 0,
                minQuantity: 0
            };
            const res = await axios.post(getApiUrl('inventory'), payload, getHeaders());

            // Refresh products
            setProducts(prev => [...prev, res.data]);
            setNewProductOpen(false);

            // Optionally clear form
            setNewProductData({ name: '', sku: '', category: 'General', costPrice: '', price: '' });

            // Ensure the new product is available immediately
            fetchData(); // Re-fetch all data to ensure consistency
        } catch (error) {
            console.error("Error creating product:", error);
            alert("Failed to create product");
        }
    };

    const handleAddItem = () => {
        setItems([...items, { productId: '', quantity: 1, unitCost: 0, subtotal: 0 }]);
    };

    const handleItemChange = (index: number, field: string, value: any) => {
        const newItems = [...items];
        newItems[index][field] = value;

        if (field === 'productId') {
            const product = products.find(p => p.id === value);
            if (product) {
                newItems[index].unitCost = product.costPrice || 0;
            }
        }

        // Recalculate subtotal
        newItems[index].subtotal = Number(newItems[index].quantity) * Number(newItems[index].unitCost);
        setItems(newItems);
    };

    const handleRemoveItem = (index: number) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
    };

    const calculateTotal = () => {
        return items.reduce((acc, item) => acc + item.subtotal, 0);
    };

    const handleSubmit = async () => {
        if (!formData.supplierId) {
            alert('Please select a supplier');
            return;
        }
        if (items.length === 0) {
            alert('Please add at least one item');
            return;
        }

        try {
            await axios.post(getApiUrl('purchases'), {
                ...formData,
                items
            }, getHeaders());
            alert('Purchase Order created successfully');
            navigate('/dashboard/purchases');
        } catch (error) {
            console.error('Error creating PO:', error);
            alert('Error creating Purchase Order');
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => navigate('/dashboard/purchases')} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                    Create Purchase Order
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Order Details</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Supplier"
                                    value={formData.supplierId}
                                    onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                                >
                                    {suppliers.map((supplier) => (
                                        <MenuItem key={supplier.id} value={supplier.id}>
                                            {supplier.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Order Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={formData.orderDate}
                                    onChange={(e) => setFormData({ ...formData, orderDate: e.target.value })}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Expected Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={formData.expectedDate}
                                    onChange={(e) => setFormData({ ...formData, expectedDate: e.target.value })}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Status"
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <MenuItem value="PENDING">Pending (Draft)</MenuItem>
                                    <MenuItem value="RECEIVED">Received (Update Stock Now)</MenuItem>
                                </TextField>
                                {formData.status === 'RECEIVED' && (
                                    <Alert severity="warning" sx={{ mt: 1, fontSize: '0.8rem' }}>
                                        Setting status to RECEIVED will immediately update stock levels and create accounting entries.
                                    </Alert>
                                )}
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Items</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button variant="outlined" onClick={() => {
                                    setNewProductData({
                                        name: '',
                                        sku: `SKU-${Date.now().toString().slice(-6)}`, // Pre-fill SKU
                                        category: 'General',
                                        costPrice: '',
                                        price: ''
                                    });
                                    setNewProductOpen(true);
                                }} startIcon={<AddIcon />}>
                                    New Product
                                </Button>
                                <Button startIcon={<AddIcon />} variant="contained" onClick={handleAddItem}>
                                    Add Line Info
                                </Button>
                            </Box>
                        </Box>

                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width="40%">Product</TableCell>
                                        <TableCell width="15%">Quantity</TableCell>
                                        <TableCell width="20%">Unit Cost</TableCell>
                                        <TableCell width="15%">Subtotal</TableCell>
                                        <TableCell width="10%"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    value={item.productId}
                                                    onChange={(e) => handleItemChange(index, 'productId', e.target.value)}
                                                >
                                                    {products.map((p) => (
                                                        <MenuItem key={p.id} value={p.id}>
                                                            {p.name} - Stock: {p.stockQuantity}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={item.quantity}
                                                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={item.unitCost}
                                                    onChange={(e) => handleItemChange(index, 'unitCost', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                ${item.subtotal.toFixed(2)}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>Total:</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                            ${calculateTotal().toFixed(2)}
                                        </TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button variant="outlined" onClick={() => navigate('/dashboard/purchases')}>Cancel</Button>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<SaveIcon />}
                                onClick={handleSubmit}
                            >
                                Create Order
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
            {/* Quick Add Product Dialog */}
            <Dialog open={newProductOpen} onClose={() => setNewProductOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Add New Product</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            label="Product Name"
                            fullWidth
                            value={newProductData.name}
                            onChange={(e) => setNewProductData({ ...newProductData, name: e.target.value })}
                        />
                        <TextField
                            label="SKU (Auto-Generated)"
                            fullWidth
                            value={newProductData.sku}
                            disabled
                            helperText="System generated unique ID"
                        />
                        <TextField
                            select
                            label="Category"
                            fullWidth
                            value={newProductData.category}
                            onChange={(e) => setNewProductData({ ...newProductData, category: e.target.value })}
                        >
                            <MenuItem value="Uniforms">Uniforms</MenuItem>
                            <MenuItem value="Stationery">Stationery</MenuItem>
                            <MenuItem value="Books">Books</MenuItem>
                            <MenuItem value="Accessories">Accessories</MenuItem>
                            <MenuItem value="General">General</MenuItem>
                        </TextField>
                        <TextField
                            label="Cost Price"
                            type="number"
                            fullWidth
                            value={newProductData.costPrice}
                            onChange={(e) => setNewProductData({ ...newProductData, costPrice: e.target.value })}
                        />
                        <TextField
                            label="Selling Price"
                            type="number"
                            fullWidth
                            value={newProductData.price}
                            onChange={(e) => setNewProductData({ ...newProductData, price: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewProductOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateProduct}>Create</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CreatePurchaseOrderPage;
